import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { $messageCentreStore } from '@lib/store';
import { useOutsideClick } from '@shared/hooks';
import { cn, DateUtils } from '@shared/utils';
import type { ThreadWithMessages } from '@shared/api';
import { MessageWrapper } from './messageWrapper';
import { Message } from './message';
import { Reply } from './reply';
import { DeleteThreadModal } from './deleteThreadModal';
import { Button, Icon, Text } from '@shared/widgets';
import MessageSquareIcon from '@assets/icons/message-square.svg';
import MoreIcon from '@assets/icons/more.svg';

interface MessageThreadProps {
  thread: ThreadWithMessages;
}

export const MessageThread = (props: MessageThreadProps) => {
  const [showMessagesList, setShowMessagesList] = useState<boolean>(false);
  const [showReply, setShowReply] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [showDeleteThreadModal, setShowDeleteThreadModal] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation('messageCenter');
  useOutsideClick(menuRef, () => setShowMenu(false));

  const deleteThreadHandler = () => {
    $messageCentreStore.events.deleteThread(props.thread.groupId);
    setShowMenu(false);
    setShowDeleteThreadModal(false);
  };

  const clickHandler = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setShowMessagesList((prev) => !prev);
    showReply && setShowReply(false);
    !props.thread.allRead && (await $messageCentreStore.events.readThread({ groupId: props.thread.groupId }));
  };

  return (
    <>
      <MessageWrapper
        className={cn('mb-0 cursor-pointer transition-shadow duration-200', 'hover:shadow-lg dark:hover:shadow-lg-dark')}
        contentClassName={cn(showMessagesList && 'border-l-2 border-l-primary', !props.thread?.allRead && 'bg-beige border border-primary')}
        onClick={clickHandler}
      >
        <div className='min-w-[0] flex-auto'>
          <Text className='mb-2' size='sm' color='gray'>
            {DateUtils.formatDate(props.thread.lastCreatedOn, { withTime: true })}
          </Text>
          <Text className='mb-2 text-xl font-bold'>{props.thread?.subject}</Text>
        </div>
        <div className='ml-5 flex flex-none flex-col items-end'>
          <div ref={menuRef} className='relative'>
            <Button
              className='h-6 w-6 p-0 text-2xl text-slate before:hidden hover:opacity-70 focus:opacity-70 dark:text-gray-white'
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu((prev) => !prev);
              }}
            >
              <Icon icon={<MoreIcon />} />
            </Button>
            <ul
              className={cn(
                showMenu
                  ? 'absolute right-0 top-full mt-3 min-w-40 cursor-pointer list-none rounded-sm bg-white py-2.5 text-sm opacity-100 shadow-[0px_0px_20px_#0000001a] transition-all duration-300 ease-out dark:bg-soft-black'
                  : 'pointer-events-none invisible absolute right-0 top-full mt-3 min-w-40 list-none rounded-sm bg-white py-2.5 text-sm opacity-0 shadow-[0px_0px_20px_#0000001a] transition-all duration-300 ease-out dark:bg-soft-black',
              )}
            >
              <li
                className='px-6 py-1 transition-colors duration-300 ease-out hover:bg-yellow-brown'
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMessagesList(true);
                  setShowReply(true);
                  setShowMenu(false);
                }}
              >
                {t('thread.menu.reply')}
              </li>
              <li
                className='px-6 py-1 text-red transition-colors duration-300 ease-out hover:bg-yellow-brown'
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteThreadModal(true);
                }}
              >
                {t('thread.menu.delete')}
              </li>
            </ul>
          </div>
          <div className='mt-auto'>
            <div className='flex items-center gap-2'>
              <Icon icon={<MessageSquareIcon />} />
              <span>{props.thread?.messages?.length || 0}</span>
            </div>
          </div>
        </div>
      </MessageWrapper>
      {showMessagesList &&
        props.thread?.messages?.map((message) => (
          <Message key={message.messageId} message={message} onClickReply={() => setShowReply((prev) => !prev)} />
        ))}
      {showReply && <Reply thread={props.thread} onReply={() => setShowReply(false)} />}
      <DeleteThreadModal open={showDeleteThreadModal} close={() => setShowDeleteThreadModal(false)} deleteThread={deleteThreadHandler} />
    </>
  );
};
